/**
 * CardViewer — карточка для повторення.
 *
 * Що вміє:
 *  • KaTeX на обох сторонах ($…$ і $$…$$) + Markdown;
 *  • лицьова сторона: питання + кнопка «Підказка»;
 *  • оборотна: відповідь + 3 кнопки оцінки пам’яті («Сложно» / «Нормально» / «Идеально»),
 *    під кожною — через скільки днів картка повернеться;
 *  • тег #наголоси → ударна буква підсвічується червоним (U+0301, див. lib/stress.js);
 *  • озвучка українським голосом Google TTS, окремо для питання і відповіді;
 *  • картинки на обох сторонах (Base64 / URL / R2);
 *  • клавіатура: Space — перевернути, 1/2/3 — оцінка, H — підказка, S — озвучити.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Eye,
  EyeOff,
  Lightbulb,
  RotateCcw,
  SkipForward,
  Volume2,
  VolumeX,
  Undo2,
} from 'lucide-react'

import RichText from './RichText.jsx'
import { GRADE_BUTTONS } from '../lib/constants.js'
import { formatInterval } from '../lib/scheduler.js'
import { hasStressTag, stressedWords } from '../lib/stress.js'
import { isSupported as speechSupported, speak, stop as stopSpeaking, speakSequence } from '../lib/speech.js'

/** Розбирає рядок тегів із D1 (GROUP_CONCAT) або масив. */
export function toTagArray(tags) {
  if (Array.isArray(tags)) return tags.map((t) => String(t).toLowerCase())
  if (!tags) return []
  return String(tags)
    .split(/[\s,]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
}

function CardImage({ src, alt }) {
  if (!src) return null
  return (
    <img
      src={src}
      alt={alt ?? ''}
      loading="lazy"
      className="mt-4 max-h-72 w-full rounded-xl border border-slate-200 bg-slate-50 object-contain
                 dark:border-slate-800 dark:bg-slate-950/60"
      onError={(e) => {
        // Бите посилання не повинно ламати картку — просто ховаємо
        e.currentTarget.style.display = 'none'
      }}
    />
  )
}

export default function CardViewer({
  card,
  /** 'srs' — з алгоритмом і оцінкою; 'free' — вільний режим; 'browse' — просто перегляд. */
  mode = 'srs',
  revealed = false,
  onReveal,
  onGrade,
  onSkip,
  onUndo,
  /** Підказки від планувальника: { hard, good, easy, again } → підпис під кнопкою. */
  preview = null,
  hintVisible = false,
  onToggleHint,
  settings,
  position = null,
  total = 0,
  canUndo = false,
  className = '',
}) {
  const tags = useMemo(() => toTagArray(card?.tags ?? card?.tag_list), [card])
  const stressCard = hasStressTag(tags)
  const hint = card?.hint?.trim() ?? ''

  const [speaking, setSpeaking] = useState(false)
  const [speechError, setSpeechError] = useState(null)
  const timerRef = useRef(null)
  const startedAtRef = useRef(Date.now())
  const hintUsedRef = useRef(false)

  const highlightStress = settings?.highlightStress !== false
  const ttsEngine = settings?.ttsEngine ?? 'google'
  const speechOn = settings?.speechEnabled !== false && speechSupported(ttsEngine)
  const rate = settings?.speechRate ?? 0.9
  const voiceName = settings?.speechVoice ?? ''

  // Нова картка → скидаємо таймер і підказку
  useEffect(() => {
    startedAtRef.current = Date.now()
    hintUsedRef.current = false
    stopSpeaking()
    setSpeaking(false)
    setSpeechError(null)
    return () => stopSpeaking()
  }, [card?.id])

  useEffect(() => {
    if (hintVisible) hintUsedRef.current = true
  }, [hintVisible])

  useEffect(
    () => () => {
      clearTimeout(timerRef.current)
      stopSpeaking()
    },
    [],
  )

  const speakText = useCallback(
    (text, { sequential = false } = {}) => {
      if (!speechOn || !text?.trim()) return
      setSpeaking(true)
      setSpeechError(null)
      const finish = () => {
        setSpeaking(false)
        setSpeechError(null)
      }
      const fail = (err) => {
        setSpeaking(false)
        setSpeechError(err?.message ?? 'Не вдалося відтворити озвучку.')
      }

      if (sequential && stressCard) {
        // Для слів з наголосами говоримо їх по черзі — так легше прослухати наголос
        const words = stressedWords(text)
        if (words.length > 1) {
          speakSequence(words, { engine: ttsEngine, rate, voiceName, onStart: () => setSpeaking(true), onEnd: finish, onError: fail })
          return
        }
      }

      speak(text, {
        engine: ttsEngine,
        rate,
        voiceName,
        onStart: () => setSpeaking(true),
        onEnd: finish,
        onError: fail,
      })
    },
    [speechOn, ttsEngine, rate, voiceName, stressCard],
  )

  const toggleSpeak = useCallback(
    (text) => {
      if (speaking) {
        stopSpeaking()
        setSpeaking(false)
      } else {
        speakText(text, { sequential: true })
      }
    },
    [speaking, speakText],
  )

  const grade = useCallback(
    (buttonKey) => {
      if (mode !== 'srs' || !onGrade) return
      onGrade(buttonKey, {
        elapsedMs: Date.now() - startedAtRef.current,
        usedHint: hintUsedRef.current,
      })
    },
    [mode, onGrade],
  )

  // ── Клавіатура ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === 'browse' || !onReveal) return

    const isTypingTarget = (el) =>
      el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)

    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingTarget(e.target)) return

      const k = e.key.toLowerCase()

      if (!revealed) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          onReveal()
        } else if (k === 'h' && hint && onToggleHint) {
          e.preventDefault()
          onToggleHint()
        } else if (k === 's' && speechOn) {
          e.preventDefault()
          toggleSpeak(card?.front)
        }
        return
      }

      const map = { 1: 'hard', 2: 'normal', 3: 'perfect' }
      if (map[k] && mode === 'srs') {
        e.preventDefault()
        grade(map[k])
      } else if (k === '4' && mode === 'srs' && settings?.showAgainButton) {
        e.preventDefault()
        onGrade?.('again', { elapsedMs: Date.now() - startedAtRef.current, usedHint: hintUsedRef.current })
      } else if (k === 's' && speechOn) {
        e.preventDefault()
        toggleSpeak(card?.back)
      } else if (e.key === ' ' && onSkip) {
        e.preventDefault()
        onSkip()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [revealed, mode, onReveal, onGrade, onSkip, onToggleHint, hint, grade, card, speechOn, toggleSpeak, settings])

  if (!card) return null

  return (
    <div className={`flex w-full flex-col items-center ${className}`}>
      {/* Прогрес сесії */}
      {total > 0 && (
        <div className="mb-3 flex w-full items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-brand-500 transition-[width] duration-300"
              style={{ width: `${total ? ((position ?? 0) / total) * 100 : 0}%` }}
            />
          </div>
          <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">
            {Math.min((position ?? 0) + 1, total)} / {total}
          </span>
        </div>
      )}

      <article
        className={`card-face animate-card-in flex w-full flex-col overflow-hidden ${
          revealed ? 'ring-2 ring-brand-400 dark:ring-brand-500' : ''
        }`}
      >
        {/* Теги + озвучка */}
        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
          {stressCard && (
            <span className="chip bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">
              наголос
            </span>
          )}
          {tags
            .filter((t) => t !== 'наголоси')
            .slice(0, 4)
            .map((t) => (
              <span key={t} className="chip">
                #{t}
              </span>
            ))}
          {card?.deck_title && <span className="chip ml-auto hidden sm:inline-flex">{card.deck_title}</span>}

          {speechOn && (
            <button
              type="button"
              onClick={() => toggleSpeak(revealed ? card.back : card.front)}
              className={`btn-ghost ml-auto !px-2 !py-1 sm:ml-0 ${speaking ? 'is-speaking animate-speak text-white' : ''}`}
              aria-label={speaking ? 'Зупинити озвучку' : 'Прослухати'}
              title="Прослухати (S)"
            >
              {speaking ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4 opacity-70" />}
            </button>
          )}
        </div>
        {speechError && (
          <p role="alert" className="w-full px-5 pt-2 text-xs text-rose-300 sm:px-7">
            {speechError}
          </p>
        )}

        {/* ЛИЦОВА СТОРОНА */}
        {!revealed ? (
          <div className="flex min-h-[16rem] flex-col px-5 py-6 sm:px-7">
            <RichText tags={tags} highlightStress={highlightStress} className="flex-1 text-lg">
              {card.front}
            </RichText>

            <CardImage src={card.image_url} alt="Ілюстрація до питання" />

            {hint && hintVisible && (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm
                              dark:border-amber-800 dark:bg-amber-950/40">
                <p className="mb-1 flex items-center gap-1.5 font-semibold text-amber-800 dark:text-amber-300">
                  <Lightbulb className="h-4 w-4" /> Підказка
                </p>
                <RichText tags={tags} highlightStress={highlightStress}>
                  {hint}
                </RichText>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              {hint && !hintVisible && (
                <button type="button" onClick={onToggleHint} className="btn-outline">
                  <Lightbulb className="h-4 w-4" /> Підказка <span className="kbd ml-1">H</span>
                </button>
              )}
              {hintVisible && hint && (
                <button type="button" onClick={onToggleHint} className="btn-ghost">
                  <EyeOff className="h-4 w-4" /> Сховати підказку
                </button>
              )}
              {onReveal && (
                <button type="button" onClick={onReveal} className="btn-primary sm:min-w-52">
                  <Eye className="h-4 w-4" /> Показати відповідь <span className="kbd ml-1">Space</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          /* ОБОРОТНА СТОРОНА */
          <>
            <div className="min-h-[10rem] border-b border-slate-100 px-5 py-5 sm:px-7 dark:border-slate-800">
              <p className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase dark:text-slate-500">
                Питання
              </p>
              <RichText tags={tags} highlightStress={highlightStress} className="text-sm text-slate-500 dark:text-slate-400">
                {card.front}
              </RichText>
            </div>

            <div className="min-h-[12rem] px-5 py-5 sm:px-7">
              <RichText tags={tags} highlightStress={highlightStress} className="text-lg">
                {card.back}
              </RichText>
              <CardImage src={card.back_image_url ?? card.image_url} alt="Ілюстрація до відповіді" />
            </div>

            {/* Оцінка пам’яті */}
            <div className="safe-bottom border-t border-slate-100 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950/50">
              {mode === 'srs' ? (
                <>
                  <p className="mb-2.5 text-center text-xs font-medium text-slate-500 dark:text-slate-400">
                    Наскільки складно було згадати?
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {GRADE_BUTTONS.map((b) => (
                      <button
                        key={b.key}
                        type="button"
                        onClick={() => grade(b.key)}
                        className={`group flex flex-col items-center gap-0.5 rounded-xl px-2 py-2.5 text-sm font-bold
                                    transition active:scale-[0.97] ${b.className}`}
                        title={b.hint}
                      >
                        <span>{b.label}</span>
                        {preview?.[b.grade] && (
                          <span className="text-[11px] font-medium opacity-80 tabular-nums">
                            {preview[b.grade]}
                          </span>
                        )}
                        <span className="kbd mt-0.5 border-white/25 bg-white/15 text-white/90">
                          {b.key === 'hard' ? 1 : b.key === 'normal' ? 2 : 3}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center justify-center gap-3 text-xs">
                    {canUndo && onUndo && (
                      <button type="button" onClick={onUndo} className="btn-ghost !px-2 !py-1 text-xs">
                        <Undo2 className="h-3.5 w-3.5" /> Скасувати
                      </button>
                    )}
                    {onSkip && (
                      <button type="button" onClick={onSkip} className="btn-ghost !px-2 !py-1 text-xs">
                        <SkipForward className="h-3.5 w-3.5" /> Пропустити
                      </button>
                    )}
                  </div>
                </>
              ) : mode === 'free' ? (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                    Вільний режим — інтервали повторення не змінюються
                  </p>
                  <div className="flex gap-2">
                    <button type="button" onClick={onSkip} className="btn-primary">
                      <RotateCcw className="h-4 w-4" /> Наступна картка
                    </button>
                    <button type="button" onClick={() => toggleSpeak(card.back)} className="btn-outline !px-3">
                      {speaking ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button type="button" onClick={onSkip} className="btn-outline">
                    <SkipForward className="h-4 w-4" /> Далі
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </article>

      {/* Підказки про інтервал */}
      {revealed && mode === 'srs' && preview && (
        <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">
          Наступний показ: {preview[settings?.showAgainButton ? 'again' : 'good']} ·{' '}
          {formatInterval(card?.interval ?? 0)} — поточний інтервал
        </p>
      )}
    </div>
  )
}
